import { isWelcomeDeferredOn } from "@/lib/login-welcome";
import type { AuthUser, ProfileField } from "@/types/auth.types";

export type Gender = "male" | "female";

/**
 * Same folding as the backend (src/common/user-gender.ts): the mobile app
 * stored "Homme" / "Femme", older accounts "M" / "F".
 */
const SPELLINGS: Record<string, Gender> = {
  f: "female",
  female: "female",
  femme: "female",
  feminin: "female",
  m: "male",
  h: "male",
  male: "male",
  homme: "male",
  masculin: "male",
};

export function normalizeGender(value: unknown): Gender | null {
  if (typeof value !== "string") return null;
  const folded = value
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .trim()
    .toLowerCase();
  return SPELLINGS[folded] ?? null;
}

type CompletionSource = Pick<AuthUser, "gender" | "country" | "missingProfileFields">;

/**
 * Fields the user still has to fill, or `undefined` when the server has not
 * said yet (a session stored before the check existed): the caller then asks
 * /auth/me. The server names the fields; a value filled since then clears
 * its field without waiting for a refresh.
 */
export function pendingProfileFields(user: CompletionSource | null | undefined): ProfileField[] | undefined {
  if (!user) return [];
  if (!Array.isArray(user.missingProfileFields)) return undefined;
  return user.missingProfileFields.filter((field) =>
    field === "gender" ? !normalizeGender(user.gender) : !String(user.country ?? "").trim(),
  );
}

/** The step waits behind the sign-in screens and the other mandatory steps. */
export function isProfileCompletionDeferredOn(pathname: string): boolean {
  return isWelcomeDeferredOn(pathname);
}
